import { url } from '../../../../api';
import { apiClient } from '../../../../api/client';
import { coffeeTypePaths } from '../../api/paths';
import type { CoffeeTypeData } from '../types';
import supermarketIcon from '../assets/supermarket-coffee.svg';

// Sentinel coffeeType for supermarket (kind === 'supermarket') ratings. Their icon is
// a bundled local asset — we never hit the GCS icon bucket for it (see getCoffeeIconUrl).
export const SUPERMARKET_ICON_KEY = 'supermarket';

const SPECIAL_FORMATS: Record<string, string> = {
    'frappe-sketo': 'Frappe',
    'freddo-esp': 'Freddo Espresso',
    'freddo-cap': 'Freddo Cappuccino',
    'iced-latte': 'Iced Latte',
    'flat-white': 'Flat White',
    greek: 'Greek Coffee',
};

let cache: Record<string, CoffeeTypeData> = {};
let fetched = false;
let inFlight: Promise<void> | null = null;

export const fetchCoffeeTypes = async (force: boolean = false): Promise<void> => {
    if (fetched && !force) return;
    if (inFlight) return inFlight;

    inFlight = (async () => {
        try {
            const response = await apiClient.get(`${url}${coffeeTypePaths.list()}`);
            if (response.data.success) {
                const next: Record<string, CoffeeTypeData> = {};
                response.data.data.forEach((c: CoffeeTypeData) => {
                    next[c.coffeeKey] = c;
                });
                cache = next;
                fetched = true;
            }
        } catch (error) {
            console.error('Failed to fetch coffee types:', error);
        } finally {
            inFlight = null;
        }
    })();

    return inFlight;
};

export const getCoffeeTypesCache = (): Record<string, CoffeeTypeData> => cache;

export const getCoffeeIconUrl = (iconName: string): string => {
    // supermarket ratings render a fully-bundled local SVG — no GCS bucket round-trip,
    // works offline and is wholly in our control.
    if (iconName === SUPERMARKET_ICON_KEY) return supermarketIcon;
    return `${url}/api/icons/icon/${iconName}`;
};

export const formatCoffeeType = (type: string): string => {
    if (cache[type]) return cache[type].displayName;
    const special = SPECIAL_FORMATS[type.toLowerCase()];
    if (special) return special;
    return type
        .split('-')
        .map(w => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
};

// kick off background load on module import
void fetchCoffeeTypes();
